// Lazy access to the county geometry layer.
//
// Nothing here is imported eagerly by a route: the map calls loadCountyShapes() only
// after a user drills into a state, and loadCountyIndex() only when they type into the
// search box. That is what keeps 3,142 county paths (~785KB across 51 files) out of
// every initial chunk while still letting the map draw real counties.
//
// Generated inputs: scripts/gen-county-geo.mjs (bun run gen:county-geo).

/** One county's outline on the shared 960x600 viewBox (US_MAP_VIEWBOX). */
export interface CountyShape {
  fips: string;
  name: string;
  /** URL slug, unique within its state — matches /states/$state/$county. */
  slug: string;
  /** SVG path data, already projected and simplified. */
  d: string;
  /** Projected centroid, for labels and marker placement. */
  cx: number;
  cy: number;
}

/** A county in the national search index — no geometry. */
export interface CountyIndexEntry {
  fips: string;
  name: string;
  slug: string;
  stateAbbr: string;
}

/** Decode a packed COUNTY_ROWS string (fips\tname\tslug\tcx\tcy\td per line). */
export function decodeCountyRows(raw: string): CountyShape[] {
  if (!raw) return [];
  const out: CountyShape[] = [];
  for (const line of raw.split("\n")) {
    const [fips, name, slug, cx, cy, d] = line.split("\t");
    if (!fips || !d) continue;
    out.push({ fips, name, slug, d, cx: Number(cx), cy: Number(cy) });
  }
  return out;
}

/** Decode the packed county index (fips\tname\tslug per line) using a FIPS→abbr map. */
export function decodeCountyIndex(
  raw: string,
  fipsToAbbr: Record<string, string>,
): CountyIndexEntry[] {
  if (!raw) return [];
  const out: CountyIndexEntry[] = [];
  for (const line of raw.split("\n")) {
    const [fips, name, slug] = line.split("\t");
    const stateAbbr = fips && fipsToAbbr[fips.slice(0, 2)];
    if (!fips || !stateAbbr) continue;
    out.push({ fips, name, slug, stateAbbr });
  }
  return out;
}

// Vite turns this glob into one lazy chunk per file, so `import()` below fetches
// exactly one state's geometry. A plain template-literal import would work too, but
// the glob makes the set of reachable chunks explicit and survives SSR bundling.
const CHUNKS = import.meta.glob<{ COUNTY_ROWS: string }>("./counties/*.ts");

/** True when we have geometry for this state (all 50 + DC; false for territories). */
export function hasCountyChunk(stateAbbr: string): boolean {
  return `./counties/${stateAbbr.toLowerCase()}.ts` in CHUNKS;
}

/** Every state abbreviation with a county chunk, uppercased and sorted. */
export function countyChunkStates(): string[] {
  return Object.keys(CHUNKS)
    .map((k) => k.slice("./counties/".length, -".ts".length).toUpperCase())
    .sort();
}

const shapeCache = new Map<string, CountyShape[]>();

/**
 * Load (and memoize) one state's county outlines. Resolves to an empty array for a
 * state we have no chunk for, so callers never need a try/catch around a drill-down.
 */
export async function loadCountyShapes(stateAbbr: string): Promise<CountyShape[]> {
  const abbr = stateAbbr.toUpperCase();
  const cached = shapeCache.get(abbr);
  if (cached) return cached;
  const loader = CHUNKS[`./counties/${abbr.toLowerCase()}.ts`];
  if (!loader) return [];
  const mod = await loader();
  const shapes = decodeCountyRows(mod.COUNTY_ROWS);
  shapeCache.set(abbr, shapes);
  return shapes;
}

let indexCache: CountyIndexEntry[] | null = null;

/** Load (and memoize) the national county search index. */
export async function loadCountyIndex(): Promise<CountyIndexEntry[]> {
  if (indexCache) return indexCache;
  const mod = await import("./county-index");
  indexCache = decodeCountyIndex(mod.COUNTY_INDEX_ROWS, mod.STATE_FIPS_TO_ABBR);
  return indexCache;
}
